import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChannelRecord, CreateChannelRequest } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useChannelsList, useCreateChannel, useDeleteChannel } from '../api/useChannels.ts';
import { useChannelModelsList } from '../api/useChannelModels.ts';
import { useVendorsList } from '../api/useVendors.ts';
import { Modal } from '../components/Modal.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';

const INPUT_STITCH =
  'w-full bg-surface-container-lowest border-none rounded-lg text-sm p-3 outline-none focus:ring-1 focus:ring-primary transition-colors';

function ChannelRow({
  row,
  zebra,
  onDelete,
  deleting,
  onNavigate,
  modelCount,
}: {
  readonly row: ChannelRecord;
  readonly zebra: 'odd' | 'even';
  readonly onDelete: (row: ChannelRecord) => void;
  readonly deleting: boolean;
  readonly onNavigate: (id: string) => void;
  readonly modelCount: number;
}): JSX.Element {
  const t = useT();
  const zebraClass = zebra === 'odd' ? '' : 'bg-surface-container-low/20';
  return (
    <tr
      data-testid={`channel-row-${row.id}`}
      className={`${zebraClass} hover:bg-surface-bright/30 transition-colors group cursor-pointer`}
      onClick={() => onNavigate(row.id)}
    >
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-on-surface">{row.display_name}</span>
          <span
            data-testid={`channel-row-${row.id}-slug`}
            className="font-mono text-[11px] text-outline"
          >
            {row.slug}
          </span>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-tertiary/10 text-tertiary text-[10px] font-bold uppercase">
          {row.vendor}
        </span>
      </td>
      <td className="px-6 py-4 text-sm font-semibold text-primary">×{row.rate_multiplier.toFixed(2)}</td>
      <td className="px-6 py-4 text-xs text-outline">
        {modelCount > 0 ? (
          <span>{modelCount} {t('channel_detail.models_count')}</span>
        ) : (
          <span className="text-outline/30">—</span>
        )}
      </td>
      <td className="px-6 py-4 text-sm">{row.supplier_name ?? <span className="text-outline/30">—</span>}</td>
      <td className="px-6 py-4 text-center">
        {row.is_official ? (
          <span aria-hidden="true" className="material-symbols-outlined text-primary text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>
            verified
          </span>
        ) : (
          <span className="text-outline/30">—</span>
        )}
      </td>
      <td className="px-6 py-4 text-xs text-outline">{row.created_at.slice(0, 10)}</td>
      <td className="px-6 py-4 text-right">
        <button
          type="button"
          onClick={() => onDelete(row)}
          disabled={deleting}
          data-testid={`channel-row-${row.id}-delete`}
          className="text-outline opacity-0 group-hover:opacity-100 hover:text-error transition-all disabled:opacity-40"
          aria-label={t('channels.row.delete')}
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">delete</span>
        </button>
        <span aria-hidden="true" className="material-symbols-outlined text-outline group-hover:text-primary transition-colors ml-2">
          chevron_right
        </span>
      </td>
    </tr>
  );
}

export function ChannelsPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { data: channels, isLoading, error } = useChannelsList();
  const { data: allChannelModels } = useChannelModelsList();
  const vendorsQ = useVendorsList();
  const createMut = useCreateChannel();
  const deleteMut = useDeleteChannel();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const channelStats = useMemo(() => {
    const stats = new Map<string, number>();
    for (const cm of allChannelModels ?? []) {
      stats.set(cm.channel_id, (stats.get(cm.channel_id) ?? 0) + 1);
    }
    return stats;
  }, [allChannelModels]);

  const filtered = useMemo(() => {
    if (!channels) return [];
    if (search.length === 0) return channels;
    const q = search.toLowerCase();
    return channels.filter(
      (c) =>
        c.slug.toLowerCase().includes(q) ||
        c.display_name.toLowerCase().includes(q) ||
        (c.supplier_name ?? '').toLowerCase().includes(q),
    );
  }, [channels, search]);

  function handleDelete(row: ChannelRecord): void {
    deleteMut.mutate(row.id, {
      onSuccess: () => toast.success(t('channels.toast.deleted')),
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function handleCreate(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body: CreateChannelRequest = {
      vendor: (fd.get('vendor') as string).trim(),
      slug: (fd.get('slug') as string).trim(),
      display_name: (fd.get('display_name') as string).trim(),
      supplier_name: ((fd.get('supplier_name') as string) || '').trim() || undefined,
      is_official: fd.get('is_official') === 'on',
      rate_multiplier: parseFloat((fd.get('rate_multiplier') as string) || '1') || 1.0,
    };
    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(t('channels.toast.created'));
        setCreateOpen(false);
      },
      onError: (err) => {
        if (err instanceof OpsApiError && err.code === 'CHANNEL_ALREADY_EXISTS') {
          toast.error(t('channels.toast.duplicate'));
        } else {
          toast.error(coerceErrorMessage(err));
        }
      },
    });
  }

  if (isLoading) {
    return (
      <div data-testid="channels-loading" className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded-xl bg-surface-container" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="channels-error" className="rounded-xl bg-error-container px-6 py-4 text-on-error-container">
        {coerceErrorMessage(error)}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-xl font-semibold text-on-surface">{t('channels.title')}</h2>
          <p className="mt-1 text-sm text-outline">{t('channels.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          data-testid="channels-create-btn"
          className="flex items-center gap-2 rounded-lg bg-primary-container px-4 py-2 text-sm font-semibold text-on-primary transition-all hover:brightness-110 active:scale-95 shadow-lg shadow-primary/10"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[18px]">add</span>
          {t('channels.create_btn')}
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('channels.search.placeholder')}
            data-testid="channels-search"
            className="w-full bg-surface-container-lowest border-none rounded-lg text-sm p-3 outline-none focus:ring-1 focus:ring-primary transition-colors"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-outline/50">
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">search</span>
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="channels-empty" className="rounded-xl bg-surface-container px-6 py-16 text-center text-outline">
          {t('channels.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-surface-container" data-testid="channels-table">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-surface-container-high/50">
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('channels.col.display_name')}</th>
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('channel_detail.add_modal.vendor')}</th>
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('channels.col.rate_multiplier')}</th>
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('channels.col.products')}</th>
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('channels.col.supplier')}</th>
                <th scope="col" className="px-6 py-4 text-center text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('channels.col.official')}</th>
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('channels.col.created_at')}</th>
                <th scope="col" className="px-6 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {filtered.map((ch, i) => {
                const mc = channelStats.get(ch.id) ?? 0;
                return (
                  <ChannelRow
                    key={ch.id}
                    row={ch}
                    zebra={i % 2 === 0 ? 'odd' : 'even'}
                    onDelete={handleDelete}
                    deleting={deleteMut.isPending}
                    onNavigate={(cid) => navigate(`/channels/${cid}`)}
                    modelCount={mc}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} titleId="channels-create-title">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <h3 id="channels-create-title" className="text-xl font-semibold text-on-surface">{t('channels.create_modal.title')}</h3>
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="text-outline hover:text-on-surface transition-colors"
            >
              <span aria-hidden="true" className="material-symbols-outlined">close</span>
            </button>
          </div>
          <form onSubmit={handleCreate} noValidate className="space-y-6">
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">{t('channel_detail.add_modal.vendor')}</label>
              <div className="relative">
                <select name="vendor" className={`${INPUT_STITCH} appearance-none`}>
                  <option value="">{t('channel_detail.add_modal.select_vendor')}</option>
                  {(vendorsQ.data ?? []).map((v) => (
                    <option key={v.slug} value={v.slug}>{v.display_name}</option>
                  ))}
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-outline pointer-events-none text-sm">expand_more</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">SLUG ({t('channels.create_modal.slug_hint')})</label>
                <input name="slug" className={`${INPUT_STITCH} font-mono`} placeholder="e.g. custom-endpoint" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">{t('channels.create_modal.supplier_name')}</label>
                <input name="supplier_name" className={INPUT_STITCH} placeholder={t('channels.create_modal.supplier_placeholder')} />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">{t('channels.create_modal.display_name')}</label>
              <input name="display_name" className={INPUT_STITCH} placeholder={t('channels.create_modal.display_name_placeholder')} />
            </div>

            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">
                {t('channels.create_modal.rate_multiplier')}
              </label>
              <input
                name="rate_multiplier"
                type="number"
                step="0.01"
                min="0.01"
                defaultValue="1.0"
                className={INPUT_STITCH}
              />
              <p className="mt-1 text-[11px] text-outline">{t('channels.create_modal.rate_multiplier_hint')}</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface-container-low rounded-lg">
              <div>
                <p className="text-sm font-medium">{t('channels.create_modal.is_official')}</p>
                <p className="text-xs text-outline">{t('channels.create_modal.is_official_hint')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input name="is_official" type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-outline-variant rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-on-surface after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
              </label>
            </div>

            {createMut.isError && (
              <div className="rounded-lg bg-error-container px-3 py-2 text-xs text-on-error-container" data-testid="channels-create-error">
                {createMut.error instanceof OpsApiError
                  ? createMut.error.message
                  : coerceErrorMessage(createMut.error)}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-on-surface-variant hover:bg-surface-variant transition-all"
              >
                {t('channels.create_modal.cancel')}
              </button>
              <button
                type="submit"
                disabled={createMut.isPending}
                className="px-8 py-2.5 rounded-lg text-sm font-semibold bg-primary-container text-on-primary hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-primary/10 disabled:opacity-40 disabled:pointer-events-none"
              >
                {createMut.isPending ? t('channels.create_modal.creating') : t('channels.create_modal.submit')}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
